/* eslint-disable @typescript-eslint/ban-ts-comment */
import { proStudioUrqlGql as urqlGql } from '@pypestream/api-services';
import { proStudioUrqlGql as agentManagerGql } from '@pypestream/api-services/urql';
import { throwErrorWithId } from '@pypestream/utils';

import { ProStudioContext, ProStudioEvents } from '../prostudio.xstate.types';
import { checkForErrors } from '../../utils/check-for-errors';
import { readLegacyBotSolutionId } from '../../utils/resolve-bot-solution-id';

type MediaAsset = { id: string; url: string; path?: string };

export const deleteAsset = async (
  ctx: ProStudioContext,
  event: ProStudioEvents
): Promise<{ selectedBot: ProStudioContext['selectedBot'] } | void> => {
  if (event.type !== 'deleteAsset') {
    return;
  }

  const { botName, customerId, selectedBot, project } = ctx;

  const projectId = project?.id;

  if (!projectId) {
    throwErrorWithId('Project ID is required');
    return;
  }

  if (!selectedBot) {
    throwErrorWithId('Selected bot is required');
  }

  const assets = (ctx.selectedBot?.assets as MediaAsset[] | undefined) ?? [];
  const withoutDeleted = {
    selectedBot: {
      ...ctx.selectedBot,
      assets: assets.filter(({ id }) => id !== event.id),
    },
  };

  // An Agent Manager v2 solution keeps its media in the solution repo's
  // `assets/` folder and has no Bot Manager counterpart, so the file is deleted
  // there, by the path `loadAgentManagerAssets` recorded for it.
  const solutionId = selectedBot?.uniqueId;
  if (!readLegacyBotSolutionId(ctx) && solutionId) {
    const filePath = assets.find(({ id }) => id === event.id)?.path;
    if (!filePath || !ctx.draftVersion) {
      throwErrorWithId('Asset path and draft version are required');
      return;
    }

    const response = await agentManagerGql.deleteAgentManagerFile({
      filePath,
      solutionId,
      versionName: ctx.draftVersion,
    });
    // Fail rather than drop the row: a delete the server refused would come
    // back on the next `assetsFiles` read.
    checkForErrors(response);

    return withoutDeleted;
  }

  if (!customerId) {
    throwErrorWithId('Customer ID is required');
  }

  if (!botName) {
    throwErrorWithId('Bot Name is required');
  }

  const botId = `${customerId}.${botName}`;

  const response = await urqlGql.deleteBotAsset({
    assetId: encodeURIComponent(event.id),
    botId,
    projectId,
  });

  // A legacy solution's media is still owned by Bot Manager. Failing here
  // instead of ignoring the result routes to the flow's "Error deleting asset"
  // toast, rather than dropping a row that would come back on the next read.
  checkForErrors(response);

  return withoutDeleted;
};
